import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  styled,
  Select,
  MenuItem,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Avatar,
  Dialog,
  DialogContent,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideocamIcon from '@mui/icons-material/Videocam'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  parse,
  isWithinInterval,
  setHours,
  setMinutes,
  addMinutes,
  startOfToday,
  getDay,
} from 'date-fns';
import { Meeting } from '../../types/meetings';
import { WorkingHours, BlockedTime } from '../../types/expert';
import { TIMEZONES } from '../../constants/timezones';
import { DURATIONS } from '../../constants/durations';

const Container = styled(Box)(({ theme }) => ({
  maxWidth: 800,
  margin: '0 auto',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const HeaderTitle = styled(Box)(({ theme }) => ({
  '& h1': {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: 500,
    margin: 0,
    marginBottom: theme.spacing(0.5),
  },
  '& p': {
    color: theme.palette.text.secondary,
    margin: 0,
    fontSize: theme.typography.body2.fontSize,
  },
}));

const CalendarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'flex-start',
  flexDirection: isMobile ? 'column' : 'row',
}));

const CalendarSection = styled(Box)(({ theme }) => ({
  flex: 1,
  width: '100%',
}));

const MonthHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  '& h2': {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: 500,
    margin: 0,
  },
}));

const WeekDaysGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  marginBottom: theme.spacing(1),
}));

const WeekDay = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  textAlign: 'center',
  padding: theme.spacing(1),
}));

const DaysGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 2,
}));

const DayButton = styled(Button, {
  shouldForwardProp: (prop) => !['isToday', 'isSelected', 'isDisabled'].includes(prop as string),
})<{ isToday?: boolean; isSelected?: boolean; isDisabled?: boolean }>(({ theme, isToday, isSelected, isDisabled }) => ({
  aspectRatio: '1',
  borderRadius: '50%',
  padding: 0,
  minWidth: 'unset',
  fontSize: theme.typography.caption.fontSize,
  color: isDisabled ? theme.palette.text.disabled :
    isSelected ? theme.palette.primary.contrastText :
      theme.palette.text.primary,
  backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
  border: isToday ? `2px solid ${theme.palette.primary.main}` : 'none',
  '&:hover': {
    backgroundColor: isDisabled ? 'transparent' :
      isSelected ? theme.palette.primary.dark :
        theme.palette.action.hover,
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
  },
}));

const TimeSlotsSection = styled(Box)(({ theme }) => ({
  flex: 1,
  width: '100%',
}));

const TimeSlotGroup = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& h3': {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 500,
    margin: `0 0 ${theme.spacing(2)} 0`,
    color: theme.palette.text.secondary,
  },
}));

const TimeSlotGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  },
}));

const TimeSlotButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  border: `1px solid ${selected ? 'transparent' : theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  fontWeight: 400,
  fontSize: theme.typography.body2.fontSize,
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
    borderColor: selected ? 'transparent' : theme.palette.primary.main,
  },
}));

const ProfileImage = styled(Avatar)(({ theme }) => ({
  width: '100px',
  height: '100px',
  borderRadius: '50px',
  objectFit: 'cover',

  [theme.breakpoints.up('md')]: {
    margin: 0,
  },
}));

const ConfirmationCard = styled(Paper)`
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SuccessIcon = styled(CheckCircleIcon)`
  font-size: 64px;
  color: ${props => props.theme.palette.success.main};
`;

const DetailText = styled(Typography)`
  color: ${props => props.theme.palette.text.secondary};
`;

interface BookingCalendarProps {
  expertName: string;
  profileImage: string;
  duration: string;
  timezone: string;
  workingHours: WorkingHours[];
  blockedTimes: BlockedTime[];
  existingMeetings: Meeting[];
  onBack: () => void;
  onSchedule: (dateTime: Date) => void;
  isPreview?: boolean;
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  expertName,
  profileImage,
  duration,
  timezone: expertTimezone,
  workingHours,
  blockedTimes,
  existingMeetings,
  onBack,
  onSchedule,
  isPreview = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showMeetingConfirm, setShowMeetingConfirm] = useState(false);
  const [formattedDate, setFormattedDate] = useState('');

  // Auto-detect customer's timezone
  const detectCustomerTimezone = (): string => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return Object.keys(TIMEZONES).includes(timeZone) ? timeZone : 'Australia/Melbourne';
    } catch (error) {
      console.warn('Failed to detect timezone:', error);
      return 'Australia/Melbourne';
    }
  };

  const [selectedTimezone, setSelectedTimezone] = useState(detectCustomerTimezone());
  const [meetingDuration, setMeetingDuration] = useState(duration);

  const convertToExpertTimezone = (date: Date): Date => {
    const expertOffset = new Date().toLocaleString('en-US', { timeZone: expertTimezone });
    const localOffset = new Date().toLocaleString();
    const diffInHours = (new Date(expertOffset).getTime() - new Date(localOffset).getTime()) / (1000 * 60 * 60);
    return new Date(date.getTime() + diffInHours * 60 * 60 * 1000);
  };

  const isTimeSlotAvailable = (date: Date, timeStr: string): boolean => {
    // Parse the time string
    const [hours, minutes] = timeStr.match(/(\d+):(\d+)/)?.slice(1).map(Number) || [0, 0];
    const isPM = timeStr.toLowerCase().includes('pm');
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && !isPM ? 0 : hours);

    const slotStart = new Date(date);
    slotStart.setHours(adjustedHours, minutes, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + 45 * 60 * 1000); // 45 mins duration

    // Check if the date is in the past
    if (slotStart < new Date()) {
      return false;
    }

    // Check if it's within working hours
    const dayOfWeek = slotStart.getDay();
    const workingHoursForDay = workingHours.find(wh => wh.daysOfWeek.includes(dayOfWeek));

    if (!workingHoursForDay) {
      console.log('No working hours for day:', dayOfWeek);
      return false;
    }

    // Parse working hours
    const [workStartHour, workStartMinute] = workingHoursForDay.start.split(':').map(Number);
    const [workEndHour, workEndMinute] = workingHoursForDay.end.split(':').map(Number);

    const workStart = new Date(date);
    workStart.setHours(workStartHour, workStartMinute, 0, 0);

    const workEnd = new Date(date);
    workEnd.setHours(workEndHour, workEndMinute, 0, 0);

    if (slotStart < workStart || slotEnd > workEnd) {
      console.log('Outside working hours:', {
        slotStart: slotStart.toLocaleTimeString(),
        slotEnd: slotEnd.toLocaleTimeString(),
        workStart: workStart.toLocaleTimeString(),
        workEnd: workEnd.toLocaleTimeString(),
      });
      return false;
    }

    // Check blocked times
    const isBlocked = blockedTimes.some(blocked => {
      if (blocked.recurring && blocked.recurrenceRule) {
        if (blocked.recurrenceRule.includes('FREQ=WEEKLY')) {
          const blockedDay = blocked.start.getDay();
          if (dayOfWeek === blockedDay) {
            const blockedStart = new Date(date);
            blockedStart.setHours(blocked.start.getHours(), blocked.start.getMinutes(), 0, 0);

            const blockedEnd = new Date(date);
            blockedEnd.setHours(blocked.end.getHours(), blocked.end.getMinutes(), 0, 0);

            const isBlockedTime = (
              (slotStart >= blockedStart && slotStart < blockedEnd) ||
              (slotEnd > blockedStart && slotEnd <= blockedEnd)
            );

            if (isBlockedTime) {
              console.log('Blocked by recurring rule:', {
                slotStart: slotStart.toLocaleTimeString(),
                slotEnd: slotEnd.toLocaleTimeString(),
                blockedStart: blockedStart.toLocaleTimeString(),
                blockedEnd: blockedEnd.toLocaleTimeString(),
              });
            }

            return isBlockedTime;
          }
        }
        return false;
      }

      const isBlockedTime = (
        (slotStart >= blocked.start && slotStart < blocked.end) ||
        (slotEnd > blocked.start && slotEnd <= blocked.end)
      );

      if (isBlockedTime) {
        console.log('Blocked by one-time block:', {
          slotStart: slotStart.toLocaleTimeString(),
          slotEnd: slotEnd.toLocaleTimeString(),
          blockedStart: blocked.start.toLocaleTimeString(),
          blockedEnd: blocked.end.toLocaleTimeString(),
        });
      }

      return isBlockedTime;
    });

    if (isBlocked) {
      return false;
    }

    // Check existing meetings
    const isBooked = existingMeetings.some(meeting => {
      const meetingOverlap = (
        (slotStart >= meeting.start && slotStart < meeting.end) ||
        (slotEnd > meeting.start && slotEnd <= meeting.end)
      );

      if (meetingOverlap) {
        console.log('Overlaps with existing meeting:', {
          slotStart: slotStart.toLocaleTimeString(),
          slotEnd: slotEnd.toLocaleTimeString(),
          meetingStart: meeting.start.toLocaleTimeString(),
          meetingEnd: meeting.end.toLocaleTimeString(),
        });
      }

      return meetingOverlap;
    });

    return !isBooked;
  };

  const generateTimeSlots = (startHour: number, endHour: number): string[] => {
    const slots: string[] = [];
    for (let hour = startHour; hour < endHour; hour++) {
      const isPM = hour >= 12;
      const displayHour = hour > 12 ? hour - 12 : hour;
      if (meetingDuration === '15') {
        slots.push(`${displayHour}:00 ${isPM ? 'pm' : 'am'}`);
        slots.push(`${displayHour}:15 ${isPM ? 'pm' : 'am'}`);
        slots.push(`${displayHour}:30 ${isPM ? 'pm' : 'am'}`);
      } else if (meetingDuration === "30") {
        slots.push(`${displayHour}:00 ${isPM ? 'pm' : 'am'}`);
        slots.push(`${displayHour}:30 ${isPM ? 'pm' : 'am'}`);
      } else if (meetingDuration === "60") {
        slots.push(`${displayHour}:00 ${isPM ? 'pm' : 'am'}`);
      }
    }
    return slots;
  };

  const availableTimeSlots = useMemo(() => {
    if (!selectedDate) return { morning: [], afternoon: [], night: [] };

    console.log('Generating slots for date:', selectedDate.toLocaleDateString());

    const slots = {
      morning: generateTimeSlots(9, 12), // 9 AM to 12 PM
      afternoon: generateTimeSlots(12, 17), // 12 PM to 5 PM
      night: generateTimeSlots(17, 21), // 5 PM to 9 PM
    };

    const available = {
      morning: slots.morning.filter(time => isTimeSlotAvailable(selectedDate, time)),
      afternoon: slots.afternoon.filter(time => isTimeSlotAvailable(selectedDate, time)),
      night: slots.night.filter(time => isTimeSlotAvailable(selectedDate, time)),
    };

    console.log('Available slots:', {
      morning: available.morning.length,
      afternoon: available.afternoon.length,
      night: available.night.length,
    });

    return available;
  }, [selectedDate, workingHours, blockedTimes, existingMeetings, meetingDuration]);

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset selected time when date changes
  };

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
  };

  const handleSchedule = () => {
    if (!selectedDate || !selectedTime) return;

    const [hours, minutes] = selectedTime.match(/(\d+):(\d+)/)?.slice(1).map(Number) || [0, 0];
    const isPM = selectedTime.toLowerCase().includes('pm');
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && !isPM ? 0 : hours);

    const selectedDateTime = new Date(selectedDate);
    const fd = new Date(selectedDate).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    setFormattedDate(fd);
    selectedDateTime.setHours(adjustedHours, minutes, 0, 0);

    // Store the selected date/time in session storage for the questionnaire
    sessionStorage.setItem('selectedMeetingDateTime', selectedDateTime.toISOString());
    sessionStorage.setItem('selectedExpert', expertName);

    setShowMeetingConfirm(true);
  };

  return (
    <Container sx={{
      opacity: isPreview ? 0.7 : 1,
      pointerEvents: isPreview ? 'none' : 'auto'
    }}>
      <Header>
        <BackButton onClick={onBack}>
          <ArrowBackIcon />
        </BackButton>
        <HeaderTitle>
          <Typography variant="h1">Select a Meeting Time</Typography>
        </HeaderTitle>
      </Header>

      <Box justifyContent="center" display="flex" alignItems="center" gap={2} my={2}>
        <ProfileImage src={profileImage} />
        <Box>
          <Typography variant='h6' component="p" fontWeight={600}>{expertName}</Typography>
          <Typography>Travel Agent</Typography>
        </Box>
      </Box>
      <Box display="flex" gap={2}>
        <VideocamIcon />
        <Typography>Video call link and/or phone contact will be provided on booking confirmation.</Typography>
      </Box>

      <Stack spacing={3} mt={3}>
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column", sm: "row"
              },
              justifyContent: {
                xs: "center", sm: "space-between"
              },
              alignItems: {
                xs: "center", sm: "stretch"
              },
              gap: 1,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography variant="subtitle1" gutterBottom color="textSecondary">
                Select your timezone:
              </Typography>
              <Select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                size={isMobile ? "small" : "medium"}
                fullWidth
                sx={{ minWidth: 200 }}
              >
                {Object.entries(TIMEZONES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography variant="subtitle1" gutterBottom color="textSecondary">
                Duration
              </Typography>
              <Select
                value={meetingDuration}
                onChange={(e) => setMeetingDuration(e.target.value)}
                size={isMobile ? "small" : "medium"}
                fullWidth
                sx={{ minWidth: 200 }}
              >
                {Object.entries(DURATIONS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        <CalendarContainer isMobile={isMobile}>
          <CalendarSection>
            <MonthHeader>
              <IconButton onClick={handlePrevMonth} size={isMobile ? "small" : "medium"}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h2">
                {format(currentDate, 'MMMM yyyy')}
              </Typography>
              <IconButton onClick={handleNextMonth} size={isMobile ? "small" : "medium"}>
                <ChevronRightIcon />
              </IconButton>
            </MonthHeader>

            <WeekDaysGrid>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <WeekDay key={day}>{day}</WeekDay>
              ))}
            </WeekDaysGrid>

            <DaysGrid>
              {Array.from({ length: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() }).map((_, i) => (
                <Box key={`empty-${i}`} />
              ))}
              {eachDayOfInterval({
                start: startOfMonth(currentDate),
                end: endOfMonth(currentDate),
              }).map(date => {
                const isToday = isSameDay(date, new Date());
                const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
                const isDisabled = date < startOfToday();
                const isAvailableDate = !isDisabled && workingHours.some(hours => {
                  const dayOfWeek = getDay(date);
                  return hours.daysOfWeek.includes(dayOfWeek);
                });

                return (
                  <DayButton
                    key={date.toISOString()}
                    onClick={() => handleDateClick(date)}
                    disabled={!isAvailableDate || isPreview}
                    isToday={isToday}
                    isSelected={isSelected}
                    isDisabled={!isAvailableDate}
                  >
                    {format(date, 'd')}
                  </DayButton>
                );
              })}
            </DaysGrid>
          </CalendarSection>

          {selectedDate && (
            <TimeSlotsSection>
              <TimeSlotGroup>
                <Typography variant="h3">
                  Available times for {format(selectedDate, 'EEEE, MMMM d')}
                </Typography>
                <TimeSlotGrid>
                  {availableTimeSlots.morning.map((slot, index) => (
                    <TimeSlotButton
                      key={index}
                      onClick={() => handleTimeClick(slot)}
                      selected={selectedTime === slot}
                      disabled={isPreview}
                      size={isMobile ? "small" : "medium"}
                    >
                      {slot}
                    </TimeSlotButton>
                  ))}
                  {availableTimeSlots.afternoon.map((slot, index) => (
                    <TimeSlotButton
                      key={index}
                      onClick={() => handleTimeClick(slot)}
                      selected={selectedTime === slot}
                      disabled={isPreview}
                      size={isMobile ? "small" : "medium"}
                    >
                      {slot}
                    </TimeSlotButton>
                  ))}
                  {availableTimeSlots.night.map((slot, index) => (
                    <TimeSlotButton
                      key={index}
                      onClick={() => handleTimeClick(slot)}
                      selected={selectedTime === slot}
                      disabled={isPreview}
                      size={isMobile ? "small" : "medium"}
                    >
                      {slot}
                    </TimeSlotButton>
                  ))}
                </TimeSlotGrid>
              </TimeSlotGroup>

              {selectedTime && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth={isMobile}
                  size={isMobile ? "large" : "medium"}
                  onClick={handleSchedule}
                  disabled={isPreview}
                  sx={{ mt: 2 }}
                >
                  Schedule Meeting
                </Button>
              )}
            </TimeSlotsSection>
          )}
        </CalendarContainer>
      </Stack>
      <Dialog open={showMeetingConfirm} fullScreen>
        <DialogContent
          style={{
            maxWidth: '600px',
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ConfirmationCard elevation={2}>
            <SuccessIcon />
            <Typography variant="h4">Meeting Scheduled!</Typography>
            <Box width="100%" paddingX={1}>
              <Typography textAlign="start">
                <b>Duration:</b> {DURATIONS[meetingDuration]}
              </Typography>
              <Typography textAlign="start">
                <b>Meeting Time:</b> {selectedTime}(EST)
              </Typography>
              <Typography textAlign="start">
                <b>Meeting Date:</b> {formattedDate}
              </Typography>
            </Box>
            <DetailText textAlign="start">
              You will receive a confirmation email and calendar invite to accept shortly with a video link and/or a phone number to use for the meeting with your Trip Expert.
            </DetailText>
            <Button
              variant="contained"
              color="primary"
              onClick={onBack}
              sx={{ mt: 2 }}
            >
              Return to Home
            </Button>
          </ConfirmationCard>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default BookingCalendar;
